import React from "react";
import { X } from "@phosphor-icons/react";

/**
 * AiBusyOverlay — full-area overlay shown over a relative-positioned
 * container while ET (the AI) is processing an async operation.
 *
 * Visually consistent with `StepTwoLoading` and `StepFour` loading:
 *   - Pulsing brand-orange halo + bobbing ET wordmark
 *   - Headline + optional hint
 *   - Loading bar at the bottom
 *   - Optional "Vorgang abbrechen" pill in the top-right corner
 *
 * The centerpiece (logo + text) uses `position: sticky` so it stays
 * visible in the viewport even when the underlying container is taller
 * than the screen and the user has scrolled deep into it.
 *
 * Usage:
 *   <div className="relative">
 *     {busy && (
 *       <AiBusyOverlay
 *         label="ET liest Ihre Dokumente…"
 *         hint="5–60 Sekunden"
 *         onCancel={() => abortController.abort()}
 *       />
 *     )}
 *     <ContentThatShouldBeLocked />
 *   </div>
 *
 * The parent MUST be `position: relative`. The overlay captures all
 * pointer events so the user cannot interact with the underlying UI
 * while the operation runs.
 */
export default function AiBusyOverlay({
  label = "ET arbeitet…",
  hint = "",
  testId,
  onCancel,
  cancelLabel = "Vorgang abbrechen",
  // Iter 19.63.55 — Optional determinate Progress (0–100). Wenn vorhanden,
  // wird statt der animierten indeterminate-Bar ein echter Fortschritts-
  // Balken angezeigt. Backend liefert via JobHandle.set_progress(pct, label).
  progress = null,
}) {
  // Clamp + Round für stabile UI; weniger Re-Renders bei kleinen Floats.
  const hasProgress = (
    progress !== null && progress !== undefined && Number.isFinite(Number(progress))
  );
  const pct = hasProgress ? Math.max(0, Math.min(100, Math.round(Number(progress)))) : null;

  return (
    <div
      data-testid={testId}
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="absolute inset-0 z-30 bg-white/85 backdrop-blur-sm"
      style={{ pointerEvents: "auto" }}
    >
      {/* Cancel pill — sticky top-right so it stays reachable on tall containers. */}
      {onCancel && (
        <div className="sticky top-3 z-10 flex justify-end pr-3">
          <button
            type="button"
            onClick={onCancel}
            data-testid={testId ? `${testId}-cancel` : undefined}
            className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 bg-white/95 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-700 shadow-sm transition-crisp hover:border-ink-400 hover:bg-white hover:text-ink-900 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
            aria-label={cancelLabel}
          >
            <X size={11} weight="bold" />
            {cancelLabel}
          </button>
        </div>
      )}

      {/* Sticky centerpiece — follows viewport scroll so it stays visible
          even on very tall containers (e.g. Step 2 with many options). */}
      <div className={`sticky flex flex-col items-center justify-center gap-5 py-10 ${onCancel ? "top-16" : "top-32"}`}>
        <span
          className="relative inline-flex h-16 w-16 items-center justify-center"
          aria-hidden="true"
        >
          <span className="absolute inset-0 rounded-full bg-brand-400/30 animate-ping" />
          <span
            className="absolute inset-1 rounded-full bg-brand-400/20"
            style={{ animation: "et-loading-pulse 1.8s ease-in-out infinite" }}
          />
          <img
            src="/et-mark-dark.png"
            alt="ET"
            className="relative h-12 w-auto"
            style={{ animation: "et-loading-bob 2.4s ease-in-out infinite" }}
            draggable={false}
          />
        </span>

        <div className="text-center px-6">
          <div
            key={label}
            className="font-cabinet text-xl sm:text-2xl tracking-tight text-ink-900 font-medium leading-tight"
            style={{ animation: "et-label-fade 360ms ease-out" }}
          >
            {label}
          </div>
          {hint && (
            <div
              key={hint}
              className="mt-2 text-[11px] uppercase tracking-widest font-semibold text-warm-500"
              style={{ animation: "et-label-fade 360ms ease-out" }}
            >
              {hint}
            </div>
          )}

          {/* Iter 19.63.55 — Determinate Progress Bar inline mit dem Label.
              Erscheint nur wenn `progress` gesetzt ist. Auf max ~340px breit
              gehalten, damit es auch auf Mobile gut wirkt. */}
          {hasProgress && (
            <div className="mt-5 mx-auto w-full max-w-[340px]">
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-warm-200"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                data-testid={testId ? `${testId}-progress` : undefined}
              >
                <div
                  className="h-full bg-brand-500 transition-all duration-500 ease-out"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-1.5 text-[11px] font-semibold tracking-wider text-ink-600 tabular-nums">
                {pct}%
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Indeterminate bar — nur als visueller Fallback wenn KEIN konkreter
          Progress-Wert vorliegt (z.B. legacy callers, Step 2). */}
      {!hasProgress && (
        <div className="absolute bottom-0 left-0 right-0 h-[3px] loading-bar-brand" />
      )}
    </div>
  );
}
